import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface BookingCalendarProps {
  apartmentId: number;
  pricePerNight: number;
  cleaningFee?: number;
  blockedDates?: string[];
  confirmedDates?: string[];
  pendingDates?: string[];
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
  onDatesSelected: (startDate: Date | null, endDate: Date | null, totalPrice: number, nights: number) => void;
}

const BookingCalendar: React.FC<BookingCalendarProps> = ({
  apartmentId,
  pricePerNight,
  cleaningFee = 0,
  blockedDates = [],
  confirmedDates = [],
  pendingDates = [],
  initialStartDate = null,
  initialEndDate = null,
  onDatesSelected
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  useEffect(() => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    if (initialStartDate) {
      setCurrentMonth(new Date(initialStartDate.getFullYear(), initialStartDate.getMonth(), 1));
    }
  }, [initialStartDate, initialEndDate]);

  const daysOfWeek = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    let startingDay = firstDay.getDay() - 1;
    if (startingDay < 0) startingDay = 6;
    return { daysInMonth, startingDay };
  };

  const formatDateStr = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateBlocked = (date: Date) => {
    const dateStr = formatDateStr(date);
    return blockedDates.includes(dateStr);
  };

  const isDateConfirmed = (date: Date) => {
    const dateStr = formatDateStr(date);
    return confirmedDates.includes(dateStr);
  };

  const isDatePending = (date: Date) => {
    const dateStr = formatDateStr(date);
    return pendingDates.includes(dateStr);
  };

  const isDateInPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isDateInRange = (date: Date) => {
    if (!startDate) return false;
    const end = endDate || hoveredDate;
    if (!end) return false;
    return date > startDate && date < end;
  };

  const isDateSelected = (date: Date) => {
    if (startDate && date.getTime() === startDate.getTime()) return true;
    if (endDate && date.getTime() === endDate.getTime()) return true;
    return false;
  };

  const handleDateClick = (day: number) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    
    if (isDateBlocked(clickedDate) || isDateInPast(clickedDate)) return;

    if (!startDate || (startDate && endDate)) {
      setStartDate(clickedDate);
      setEndDate(null);
    } else if (clickedDate > startDate) {
      // Check if any blocked date is in range
      let hasBlockedInRange = false;
      const tempDate = new Date(startDate);
      while (tempDate <= clickedDate) {
        if (isDateBlocked(tempDate)) {
          hasBlockedInRange = true;
          break;
        }
        tempDate.setDate(tempDate.getDate() + 1);
      }
      
      if (hasBlockedInRange) {
        setStartDate(clickedDate);
        setEndDate(null);
      } else {
        setEndDate(clickedDate);
      }
    } else {
      setStartDate(clickedDate);
      setEndDate(null);
    }
  };

  const calculateNights = () => {
    if (!startDate || !endDate) return 0;
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const calculateTotalPrice = () => {
    const nights = calculateNights();
    return (nights * pricePerNight) + cleaningFee;
  };

  useEffect(() => {
    const nights = (() => {
      if (!startDate || !endDate) return 0;
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    })();
    const total = (nights * pricePerNight) + cleaningFee;
    onDatesSelected(startDate, endDate, total, nights);
  }, [startDate, endDate, pricePerNight, cleaningFee, onDatesSelected]);

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
  const days = [];

  // Empty cells for days before month starts
  for (let i = 0; i < startingDay; i++) {
    days.push(<div key={`empty-${i}`} className="h-10"></div>);
  }

  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const blocked = isDateBlocked(date);
    const past = isDateInPast(date);
    const confirmed = isDateConfirmed(date);
    const pending = isDatePending(date);
    const selected = isDateSelected(date);
    const inRange = isDateInRange(date);
    const available = !blocked && !past;

    let className = 'h-10 w-10 flex items-center justify-center rounded-full text-sm transition-colors ';
    let title = '';
    
    if (past) {
      // Dates passées - gris clair
      className += 'text-neutral-300 cursor-not-allowed';
      title = 'Date passée';
    } else if (confirmed) {
      // Dates confirmées - gris foncé, non sélectionnable
      className += 'bg-neutral-400 text-white cursor-not-allowed';
      title = 'Réservé (confirmé)';
    } else if (pending) {
      // Dates en attente - jaune, non sélectionnable
      className += 'bg-amber-400 text-amber-900 cursor-not-allowed';
      title = 'En attente de paiement';
    } else if (selected) {
      // Dates sélectionnées
      className += 'bg-primary-500 text-white font-semibold cursor-pointer';
      title = 'Sélectionné';
    } else if (inRange) {
      // Dans la plage sélectionnée
      className += 'bg-primary-100 text-primary-700 cursor-pointer';
    } else {
      // Disponible - vert léger au survol
      className += 'bg-green-50 text-green-700 hover:bg-green-200 cursor-pointer border border-green-200';
      title = 'Disponible';
    }

    days.push(
      <div
        key={day}
        className={className}
        title={title}
        onClick={() => handleDateClick(day)}
        onMouseEnter={() => available && startDate && !endDate && setHoveredDate(date)}
        onMouseLeave={() => setHoveredDate(null)}
      >
        {day}
      </div>
    );
  }

  const formatDate = (date: Date | null) => {
    if (!date) return '--';
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR').format(price);

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPreviousMonth}
          className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="font-semibold text-lg">
          {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button
          onClick={goToNextMonth}
          className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {daysOfWeek.map((day) => (
          <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-neutral-500">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days}
      </div>

      {/* Légende des couleurs */}
      <div className="flex flex-wrap gap-4 mt-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full bg-green-50 border border-green-200"></div>
          <span className="text-neutral-600">Disponible</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full bg-amber-400"></div>
          <span className="text-neutral-600">En attente</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full bg-neutral-400"></div>
          <span className="text-neutral-600">Réservé</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full bg-primary-500"></div>
          <span className="text-neutral-600">Sélectionné</span>
        </div>
      </div>

      {/* Selected dates summary */}
      <div className="mt-6 p-4 bg-neutral-50 rounded-lg space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-neutral-600">Arrivée</span>
          <span className="font-medium">{formatDate(startDate)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-neutral-600">Départ</span>
          <span className="font-medium">{formatDate(endDate)}</span>
        </div>
        
        {startDate && endDate && (
          <>
            <hr className="border-neutral-200" />
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">{formatPrice(pricePerNight)} FCFA × {calculateNights()} nuits</span>
              <span>{formatPrice(pricePerNight * calculateNights())} FCFA</span>
            </div>
            {cleaningFee > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Frais de ménage</span>
                <span>{formatPrice(cleaningFee)} FCFA</span>
              </div>
            )}
            <hr className="border-neutral-200" />
            <div className="flex justify-between font-semibold">
              <span>Total</span>
              <span className="text-primary-600">{formatPrice(calculateTotalPrice())} FCFA</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingCalendar;
